import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import useTokenStore from "../store/store";
import useThemeStore from "../store/themeStore";
import { ThemeProps } from "../utils/theme";
import { forgetPassword, ResultMessageProps } from "../utils/user";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import Footer from "../components/headerFooter/Footer";

type Inputs = {
  email: string;
};

function Resetpwd() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<ResultMessageProps>({
    message: "",
    status: 0,
  });

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = async (data: Inputs) => {
    setLoading(true);
    const response = await forgetPassword(data.email);
    console.log('Response received:', response); // Debugging log
    setMessage(response);
    setLoading(false);

    if (response.status === 404 || response.status === 400) {
      // Email doesn't exist
      setTimeout(() => {
        setMessage({ message: "", status: 0 });
      }, 5000);
    } else if (response.status === 200) {
      // Email exists and reset link sent
      setTimeout(() => {
        setMessage({ message: "", status: 0 });
        navigate("/login");
      }, 5000);
    }
  };

  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ThemeProps
  );

  const getToken = useTokenStore((state) => state.token);
  useEffect(() => {
    if (getToken) {
      navigate("/listing");
    }
  }, [getToken, navigate]);

  useEffect(() => {
    const setBodyStyles = () => {
      document.body.style.display = "flex";
      document.body.style.flexDirection = "column";
      document.body.style.minHeight = "100vh";
      document.body.style.margin = "0";
      document.body.style.position = "relative";
      document.body.style.overflow = "auto";
    };

    setBodyStyles();

    return () => {
      document.body.style.display = "";
      document.body.style.flexDirection = "";
      document.body.style.minHeight = "";
      document.body.style.margin = "";
      document.body.style.position = "";
      document.body.style.overflow = "";
    };
  }, []);

  const isMobile = window.innerWidth <= 768;

  // White
   // White
   const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
  
    // 1) explicit white checks
    if (
      s === "white" ||
      s === "#fff" ||
      s === "#ffffff" ||
      s === "rgb(255,255,255)" ||
      s === "rgba(255,255,255,1)"
    ) {
      return true;
    }
  
    let r: number, g: number, b: number;
  
    // 2) hex formats
    if (s.startsWith("#")) {
      let hex = s.slice(1);
      if (hex.length === 3) {
        // expand `#abc` → `#aabbcc`
        hex = hex.split("").map(ch => ch + ch).join("");
      }
      if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) {
        return false;
      }
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
    // 3) rgb/rgba formats
    else {
      const m = s.match(
        /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)/
      );
      if (!m) return false;
      r = Number(m[1]);
      g = Number(m[2]);
      b = Number(m[3]);
    }
  
    // 4) threshold for "almost white"
    return r >= 235 && g >= 235 && b >= 235;
  };
  


  return (
    <div
      className="resetpwd"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        position: "relative",
        overflow: "auto",
      }}
    >
      <Helmet>
        <title>Reset Password | {getThemes && getThemes.name}</title>
      </Helmet>

      {/* Background Pattern */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${backgroundPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "60px 60px",
          zIndex: -1,
        }}
      ></div>

      {/* Fade Overlay */}
      {message.status !== 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent black
            zIndex: 999, // Ensure it's above other content
            opacity: 1,
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: isMobile ? "160px 20px 80px" : "100px 20px 80px", // Add padding on mobile devices
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white flex flex-wrap justify-center pt-8 pb-8 px-4"
          style={{
            width: "85%",
            maxWidth: "500px",
            border: "1px solid #ddd",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            zIndex: 1,
            backgroundColor: "white",
            marginBottom: "20px",
          }}
        >
          <div className="w-full">
            <p className="text-3xl font-roboto font-medium pb-8 pt-8 text-center">Reset Password</p>
            <div className="w-full py-2">
              <label
                htmlFor="input-label-with-helper-text"
                className="block text-base font-medium mb-2 text-left"
                style={{
                  color: "black",
                  fontSize: "1rem",
                  fontWeight: "500",
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                id="input-label-with-helper-text"
                className="py-3 px-4 block w-full rounded-md text-sm"
                style={{
                  border: "1px solid black",
                  borderRadius: "0.375rem",
                  padding: "0.75rem",
                  color: "#2D3748",
                  backgroundColor: "#FFF",
                }}
                placeholder="Email Address"
                aria-describedby="hs-input-helper-text"
                {...register("email", { required: true })}
              />

              <small className="flex justify-start pt-3 text-red-hover-ce312b">
                {errors.email && <span>Email Address is required</span>}
              </small>
            </div>

            <div className="text-center">
              <button
                type="submit"
                style={{
                  width: "30%",
                  background: isWhiteOrShade(getThemes?.color2) ? "black" : getThemes.color2,
                  color: isWhiteOrShade(getThemes.color1) ? "white" : getThemes.txtColor, // Text color logic
                  padding: "10px",
                  borderRadius: "5px",
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.3s ease",
                  boxSizing: "border-box",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = isWhiteOrShade(getThemes?.color1) ? "black" : getThemes.color1; // Use black if color2 is white or near-white
                  e.currentTarget.style.color = isWhiteOrShade(getThemes?.color1) ? "white" : getThemes.txtColor; // Adjust text color on hover
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = isWhiteOrShade(getThemes?.color2) ? "black" : getThemes.color2;
                  e.currentTarget.style.color = isWhiteOrShade(getThemes.color1) ? "white" : getThemes.txtColor; // Adjust text color on mouse out
                }}
              >
                Submit
                {loading && (
                  <span
                    className="animate-spin border-btnscolor inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full"
                    role="status"
                    aria-label="loading"
                    style={{ marginLeft: "10px" }}
                  ></span>
                )}
              </button>

              <Link to="/register">
                <button
                  type="button"
                  className="w-full inline-flex justify-center gap-2 font-medium tracking-wide hover:underline py-2"
                >
                  Do not have an account? Sign Up Here
                </button>
              </Link>
            </div>
          </div>
        </form>

        {/* Toast Notification */}
        {message.status !== 0 && (
          <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ zIndex: 1000, backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          >
            <div
              className="bg-white p-6 rounded shadow-md text-center"
              style={{
                width: "90%",
                maxWidth: "400px",
                backgroundColor: "white",
                zIndex: 1001, // Ensure it's above the overlay
              }}
            >
              <h2 className="text-xl font-bold mb-4">
                {message.status === 404 || message.status === 400
                  ? "Sorry but there is no account currently using this email"
                  : "An email has been sent to reset your password"}
              </h2>
              <button
                onClick={() => setMessage({ message: "", status: 0 })}
                style={{
                  width: "30%",
                  background: isWhiteOrShade(getThemes?.color2) ? "black" : getThemes.color2,
                  color: isWhiteOrShade(getThemes.color1) ? "white" : getThemes.txtColor, // Text color logic
                  padding: "10px",
                  borderRadius: "5px",
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.3s ease",
                  boxSizing: "border-box",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = isWhiteOrShade(getThemes?.color1) ? "black" : getThemes.color1; // Use black if color2 is white or near-white
                  e.currentTarget.style.color = isWhiteOrShade(getThemes?.color1) ? "white" : getThemes.txtColor; // Adjust text color on hover
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = isWhiteOrShade(getThemes?.color2) ? "black" : getThemes.color2;
                  e.currentTarget.style.color = isWhiteOrShade(getThemes.color1) ? "white" : getThemes.txtColor; // Adjust text color on mouse out
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer /> {/* Include the Footer component here */}
    </div>
  );
}

export default Resetpwd;
